'use client';

import type { UseQueryOptions } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { contentfulClient } from '../';
import type { ReleaseNote, ReleaseNoteSkeleton } from '../types';
import { transformReleaseNoteEntry } from '../utils';

interface LatestReleaseNotesOptions {
  readonly limit?: number;
}

/**
 * Fetches the latest release notes from Contentful
 *
 * @param options - Options for filtering and limiting results
 * @returns Array of latest release notes
 */
async function fetchLatestReleaseNotes(
  options: LatestReleaseNotesOptions = {}
): Promise<ReleaseNote[]> {
  // Return empty array if Contentful is not configured
  if (!contentfulClient) {
    console.warn(
      'Contentful client is not configured. Returning empty release notes.'
    );
    return [];
  }

  const { limit = 3 } = options;

  const query: Record<string, unknown> = {
    content_type: 'releaseNote',
    limit,
    order: '-fields.date',
  };

  try {
    const response =
      await contentfulClient.getEntries<ReleaseNoteSkeleton>(query);

    // Transform entries and filter out any that fail validation
    const notes: ReleaseNote[] = [];

    for (const entry of response.items) {
      try {
        notes.push(transformReleaseNoteEntry(entry));
      } catch (error) {
        console.error('Failed to transform release note entry:', error);
        // Skip this entry and continue with others
      }
    }

    return notes;
  } catch (error) {
    console.error(
      'Failed to fetch latest release notes from Contentful:',
      error
    );
    return [];
  }
}

/**
 * Hook to fetch the latest release notes from Contentful
 *
 * @param options - Options for filtering and limiting results
 * @param reactQueryOptions - Additional React Query options
 * @returns UseQueryResult containing the latest release notes
 *
 * @example
 * ```tsx
 * // Get the 3 most recent release notes
 * const { data: latestNotes } = useGetLatestReleaseNotes({ limit: 3 });
 * ```
 */
export function useGetLatestReleaseNotes(
  options: LatestReleaseNotesOptions = {},
  reactQueryOptions?: Omit<
    UseQueryOptions<ReleaseNote[], Error>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery<ReleaseNote[], Error>({
    queryKey: ['contentful', 'latest-release-notes', options],
    queryFn: () => fetchLatestReleaseNotes(options),
    staleTime: 1000 * 60 * 10, // 10 minutes - latest notes don't change frequently
    refetchOnWindowFocus: false,
    ...reactQueryOptions,
  });
}
